import { useQuery } from '@tanstack/react-query';
import type { ProductBookingGroup, ProductCategory, ProductTag, ProductUnit } from '@kontor/shared';
import { api } from '../../api';
import { useSession } from '../../session';

/**
 * Kategorien, Einheiten und Tags für Liste, Formular und Ansicht der Produkte. Gelesen mit `products.read`. Die Einrichtung nutzt eigene Abfragen (`/manage`),
 * weil der Technische Manager die Einstellungen pflegt, ohne Produkte zu sehen.
 */
function useLookup<T>(path: string) {
  const { can, hasModule } = useSession();
  const query = useQuery({
    queryKey: [path],
    queryFn: async () => (await api<{ data: T[] }>('GET', `/${path}`)).data,
    enabled: hasModule('products') && can('products.read'),
    staleTime: 5 * 60_000,
  });
  return query.data ?? [];
}

export function useProductLookups() {
  const categories = useLookup<ProductCategory>('product-categories');
  const units = useLookup<ProductUnit>('product-units');
  const tags = useLookup<ProductTag>('product-tags');
  const bookingGroups = useLookup<ProductBookingGroup>('product-booking-groups');
  const categoryLabel = (id: string | null) => {
    const c = categories.find((x) => x.id === id);
    if (!c) return null;
    const parent = c.parentId ? categories.find((x) => x.id === c.parentId) : null;
    return parent ? `${parent.name} › ${c.name}` : c.name;
  };
  return { categories, units, tags, bookingGroups, categoryLabel };
}

/** Das Bild eines Produkts. Die API liefert es nur angemeldeten Benutzern mit Leserecht, der Browser schickt das Cookie mit. */
export const imageUrl = (productId: string, imageId: string) => `/api/v1/products/${productId}/images/${imageId}`;

/** Gramm als kg mit bis zu drei Nachkommastellen, Millimeter als cm mit einer */
export const kgText = (g: number | null) => (g === null ? '' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3, useGrouping: false }).format(g / 1000));
export const cmText = (mm: number | null) => (mm === null ? '' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1, useGrouping: false }).format(mm / 10));
